<template>
  <div class="tab-shell">
    <div class="tab-head">
      <h2 class="tab-title">Notas Fiscais</h2>
      <button class="btn-primary" type="button" @click="$emit('import-nf')">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
        Importar NF
      </button>
    </div>

    <!-- Status filter tabs -->
    <div class="filter-tabs">
      <button
        v-for="f in statusFilters"
        :key="f.value"
        class="filter-tab"
        :class="{ active: statusFilter === f.value }"
        type="button"
        @click="statusFilter = f.value"
      >{{ f.label }}</button>
    </div>

    <div v-if="loading" class="empty-hint">Carregando...</div>
    <div v-else-if="filteredNfs.length === 0" class="empty-hint">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      Nenhuma nota fiscal encontrada.
    </div>
    <div v-else class="nf-list">
      <article v-for="nf in filteredNfs" :key="nf.id" class="nf-card">
        <div class="nf-card-header" @click="toggleExpand(nf.id)">
          <div class="nf-card-main">
            <div class="nf-row">
              <span class="nf-numero">NF {{ nf.numero }}<span v-if="nf.serie"> · Série {{ nf.serie }}</span></span>
              <span class="status-chip" :class="statusClass(nf.status)">{{ statusLabel(nf.status) }}</span>
            </div>
            <div class="nf-meta">{{ nf.emitenteNome || nf.emitenteCNPJ || '—' }}</div>
            <div class="nf-row nf-row--sub">
              <span class="nf-meta">{{ formatDate(nf.dataEmissao) }}</span>
              <span v-if="nf.projeto" class="nf-meta">· {{ nf.projeto }}</span>
              <span class="nf-valor">{{ formatCurrency(nf.valorTotal) }}</span>
            </div>
          </div>
          <svg class="expand-icon" :class="{ expanded: expanded.has(nf.id) }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <!-- Expanded: items + status change -->
        <div v-if="expanded.has(nf.id)" class="nf-expanded">
          <div v-if="loadingItems.has(nf.id)" class="items-loading">Carregando itens...</div>
          <template v-else>
            <div v-if="(itemsMap[nf.id] || []).length === 0" class="items-empty">Sem itens registrados.</div>
            <table v-else class="items-table">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Qtd</th>
                  <th>Vlr Unit.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="it in itemsMap[nf.id]" :key="it.id">
                  <td>{{ it.descricao }}</td>
                  <td>{{ it.quantidade }}</td>
                  <td>{{ formatCurrency(it.valorUnitario) }}</td>
                  <td>{{ formatCurrency(it.quantidade * it.valorUnitario) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="status-change-row">
              <label class="status-label-inline">Atualizar status:</label>
              <select class="status-select" v-model="statusChangeMap[nf.id]">
                <option value="">Selecionar...</option>
                <option v-for="s in nextStatuses(nf.status)" :key="s.value" :value="s.value">{{ s.label }}</option>
              </select>
              <button
                class="btn-status-save"
                type="button"
                :disabled="!statusChangeMap[nf.id] || savingStatus.has(nf.id)"
                @click="saveStatus(nf.id)"
              >{{ savingStatus.has(nf.id) ? '...' : 'Salvar' }}</button>
            </div>
          </template>
        </div>
      </article>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, reactive } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'

type NfStatus = 'recebida' | 'conferida' | 'pendente_pagamento' | 'paga' | 'cancelada'

interface NotaFiscal {
  id: string; numero: string; serie: string; dataEmissao: string
  emitenteNome: string; emitenteCNPJ: string; valorTotal: number
  status: NfStatus; projeto: string
}
interface NfItem { id: string; descricao: string; quantidade: number; valorUnitario: number }

const STATUS_LABELS: Record<string, string> = {
  recebida: 'Recebida', conferida: 'Conferida',
  pendente_pagamento: 'Pendente Pagamento', paga: 'Paga', cancelada: 'Cancelada'
}
const STATUS_CLASS: Record<string, string> = {
  recebida: 'chip--blue', conferida: 'chip--yellow',
  pendente_pagamento: 'chip--orange', paga: 'chip--green', cancelada: 'chip--red'
}
const NEXT_STATUS_MAP: Record<string, NfStatus[]> = {
  recebida: ['conferida', 'cancelada'],
  conferida: ['pendente_pagamento', 'cancelada'],
  pendente_pagamento: ['paga', 'cancelada'],
  paga: [],
  cancelada: []
}

export default defineComponent({
  name: 'OfficeNotasFiscaisTab',
  emits: ['import-nf'],
  setup() {
    const nfs = ref<NotaFiscal[]>([])
    const loading = ref(false)
    const statusFilter = ref('')
    const expanded = ref(new Set<string>())
    const loadingItems = ref(new Set<string>())
    const savingStatus = ref(new Set<string>())
    const itemsMap = reactive<Record<string, NfItem[]>>({})
    const statusChangeMap = reactive<Record<string, string>>({})

    const statusFilters = [
      { value: '', label: 'Todas' },
      { value: 'recebida', label: 'Recebida' },
      { value: 'conferida', label: 'Conferida' },
      { value: 'pendente_pagamento', label: 'Pendente Pagamento' },
      { value: 'paga', label: 'Paga' },
      { value: 'cancelada', label: 'Cancelada' }
    ]

    const filteredNfs = computed(() =>
      statusFilter.value ? nfs.value.filter(n => n.status === statusFilter.value) : nfs.value
    )

    const load = async () => {
      loading.value = true
      try { nfs.value = await apiFetch<NotaFiscal[]>('/office/notas-fiscais') }
      catch { nfs.value = [] }
      finally { loading.value = false }
    }

    const toggleExpand = async (id: string) => {
      const next = new Set(expanded.value)
      if (next.has(id)) { next.delete(id); expanded.value = next; return }
      next.add(id); expanded.value = next
      if (!itemsMap[id]) {
        const ls = new Set(loadingItems.value); ls.add(id); loadingItems.value = ls
        try {
          itemsMap[id] = await apiFetch<NfItem[]>(`/office/notas-fiscais/${id}/items`)
        } catch { itemsMap[id] = [] }
        finally { const ls2 = new Set(loadingItems.value); ls2.delete(id); loadingItems.value = ls2 }
      }
    }

    const saveStatus = async (id: string) => {
      const newStatus = statusChangeMap[id]
      if (!newStatus) return
      const ss = new Set(savingStatus.value); ss.add(id); savingStatus.value = ss
      try {
        await apiFetch(`/office/notas-fiscais/${id}/status`, { method: 'POST', body: JSON.stringify({ status: newStatus }) })
        uiStore.toast('Status atualizado.', 'success')
        statusChangeMap[id] = ''
        await load()
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao atualizar.', 'error') }
      finally { const ss2 = new Set(savingStatus.value); ss2.delete(id); savingStatus.value = ss2 }
    }

    const statusLabel = (s: string) => STATUS_LABELS[s] || s
    const statusClass = (s: string) => STATUS_CLASS[s] || ''
    const nextStatuses = (s: NfStatus) => (NEXT_STATUS_MAP[s] || []).map(v => ({ value: v, label: STATUS_LABELS[v] }))

    const formatDate = (d: string) => {
      const m = `${d || ''}`.match(/(\d{4})-(\d{2})-(\d{2})/)
      return m ? `${m[3]}/${m[2]}/${m[1]}` : d
    }
    const formatCurrency = (v: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

    onMounted(load)
    return {
      nfs, loading, statusFilter, statusFilters, filteredNfs,
      expanded, loadingItems, savingStatus, itemsMap, statusChangeMap,
      toggleExpand, saveStatus, statusLabel, statusClass, nextStatuses,
      formatDate, formatCurrency
    }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  font-size: 13px; font-weight: 600;
  &:hover { opacity: .88; }
}
.filter-tabs { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 20px; }
.filter-tab {
  padding: 6px 13px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.12);
  background: #fff; cursor: pointer; font-size: 12px; font-weight: 500; color: var(--ink-muted, #666);
  transition: all .12s;
  &:hover { background: rgba(31,30,26,.04); }
  &.active { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); border-color: transparent; }
}
.empty-hint {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px;
  svg { opacity: .35; }
}
.nf-list { display: flex; flex-direction: column; gap: 10px; }
.nf-card { border: 1px solid rgba(31,30,26,.1); border-radius: 12px; background: var(--card, #fff); overflow: hidden; }
.nf-card-header {
  display: flex; align-items: flex-start; gap: 10px; padding: 14px 16px; cursor: pointer;
  &:hover { background: rgba(31,30,26,.02); }
}
.nf-card-main { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.nf-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.nf-row--sub { justify-content: space-between; }
.nf-numero { font-size: 14px; font-weight: 700; color: var(--ink, #2c2b27); }
.nf-meta { font-size: 12px; color: var(--ink-muted, #888); }
.nf-valor { font-size: 14px; font-weight: 700; color: var(--ink, #2c2b27); margin-left: auto; }
.expand-icon {
  flex-shrink: 0; opacity: .5; transition: transform .18s; margin-top: 2px;
  &.expanded { transform: rotate(180deg); }
}
.status-chip {
  display: inline-block; font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 20px;
  &.chip--blue    { background: #dbeafe; color: #1d4ed8; }
  &.chip--yellow  { background: #fef9c3; color: #854d0e; }
  &.chip--orange  { background: #ffedd5; color: #c2410c; }
  &.chip--green   { background: #dcfce7; color: #15803d; }
  &.chip--red     { background: #fef2f2; color: #dc2626; }
}
.nf-expanded { padding: 0 16px 16px; border-top: 1px solid rgba(31,30,26,.07); }
.items-loading { font-size: 13px; color: var(--ink-muted, #888); padding: 12px 0; }
.items-empty { font-size: 13px; color: var(--ink-muted, #aaa); padding: 12px 0; }
.items-table {
  width: 100%; border-collapse: collapse; font-size: 12px; margin: 12px 0;
  th { text-align: left; padding: 6px 8px; font-weight: 600; color: var(--ink-muted, #888); border-bottom: 1.5px solid rgba(31,30,26,.1); }
  td { padding: 7px 8px; border-bottom: 1px solid rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
  tr:last-child td { border-bottom: none; }
}
.status-change-row { display: flex; align-items: center; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
.status-label-inline { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.status-select {
  padding: 6px 10px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.15);
  font-size: 12px; outline: none; background: #fff;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.btn-status-save {
  padding: 6px 14px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: #fff; font-size: 12px; font-weight: 600;
  &:disabled { opacity: .5; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: .88; }
}
@media (max-width: 600px) { .nf-row--sub { flex-direction: column; align-items: flex-start; } }
</style>
